/**
 * InlineTimeSeriesChart — Self-contained chart widget that fetches its own data
 *
 * Renders an inline time-series visualization with time range selector.
 * Dark themed to match the dashboard aesthetic.
 *
 * Phase 5 (djin-cloud#67) additions — all optional / backward-compatible:
 *   - `chartType === 'bar'` routes to the `BarChart` primitive.
 *   - `intervalOverride` lets a parent (e.g. ChartCard) own the interval state.
 *     When present, the built-in range selector is suppressed and the
 *     shorthand is mapped to endpoint interval + a sensible query window.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { subHours, subDays } from 'date-fns';
import { LineChart } from './LineChart';
import { AreaChart } from './AreaChart';
import { BarChart } from './BarChart';
import api from '../../services/api';
import type { AssetType, ChartIntervalShorthand, ChartScope } from '../../types/chart.types';

type TimeRange = '1h' | '24h' | '7d';

interface InlineTimeSeriesChartProps {
  siteId: string;
  /** Widened from `AssetType` to `ChartScope` so site-level presets (`'site'`) type-check. Existing callers pass an `AssetType` which still satisfies the union. */
  assetType: ChartScope | AssetType;
  kpiName: string;
  title: string;
  color: string;
  unit: string;
  chartType?: 'line' | 'area' | 'bar';
  height?: number;
  yAxisDomain?: [number, number] | ['auto', 'auto'];
  defaultRange?: TimeRange;
  showRangeSelector?: boolean;
  showLegend?: boolean;
  /**
   * Phase 5: when present, the built-in range selector is hidden and this
   * value is used to drive the query interval + window. `undefined` preserves
   * the pre-Phase-5 default of showing the built-in range selector.
   */
  intervalOverride?: ChartIntervalShorthand;
  multiSeries?: Array<{
    kpiName: string;
    name: string;
    color: string;
    unit: string;
  }>;
}

/**
 * Map `TimeRange` (legacy) → { start, end, interval }.
 */
function getTimeParams(range: TimeRange) {
  const end = new Date();
  const start = range === '1h'
    ? subHours(end, 1)
    : range === '24h'
    ? subHours(end, 24)
    : subDays(end, 7);
  const interval = range === '1h' ? '1min' : range === '24h' ? '5min' : '1hour';
  return { start, end, interval };
}

/**
 * Map a historical-range token (`intervalOverride`, Phase 5) → { start, end,
 * interval }. The token names the query WINDOW; each carries a downsample
 * bucket sized to keep the point count modest:
 *   - `1h`  → last 1 hour,   10s buckets   (~360 points)
 *   - `6h`  → last 6 hours,  1min buckets  (~360 points)
 *   - `24h` → last 24 hours, 5min buckets  (~288 points)
 *   - `30d` → last 30 days,  1hour buckets (~720 points)
 * `interval` values are no-space PostgreSQL interval literals (`10s`, `1min`,
 * `5min`, `1hour`) — passed verbatim to `time_bucket()` by the
 * `/v1/sites/:id/timeseries` route. No-space so they survive the hand-built
 * query string without URL-encoding, matching the legacy `getTimeParams`.
 */
function getTimeParamsFromShorthand(shorthand: ChartIntervalShorthand) {
  const end = new Date();
  let start: Date;
  let interval: string;
  switch (shorthand) {
    case '1h':
      start = subHours(end, 1);
      interval = '10s';
      break;
    case '6h':
      start = subHours(end, 6);
      interval = '1min';
      break;
    case '24h':
      start = subHours(end, 24);
      interval = '5min';
      break;
    case '30d':
      start = subDays(end, 30);
      interval = '1hour';
      break;
  }
  return { start, end, interval };
}

export function InlineTimeSeriesChart({
  siteId,
  assetType,
  kpiName,
  title,
  color,
  unit,
  chartType = 'area',
  height = 220,
  yAxisDomain = ['auto', 'auto'],
  defaultRange = '24h',
  showRangeSelector = true,
  showLegend = false,
  intervalOverride,
  multiSeries,
}: InlineTimeSeriesChartProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>(defaultRange);

  const effectiveRangeSelectorVisible = showRangeSelector && !intervalOverride;

  const allKpis = multiSeries
    ? multiSeries.map(s => s.kpiName).join(',')
    : kpiName;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['inline-ts', siteId, assetType, allKpis, intervalOverride ?? timeRange],
    queryFn: async () => {
      const { start, end, interval } = intervalOverride
        ? getTimeParamsFromShorthand(intervalOverride)
        : getTimeParams(timeRange);
      const response = await api.get(
        `/v1/sites/${siteId}/timeseries?` +
        `asset_type=${assetType}&` +
        `kpis=${allKpis}&` +
        `start=${start.toISOString()}&` +
        `end=${end.toISOString()}&` +
        `interval=${interval}`
      );
      return response.data.data.series;
    },
    refetchInterval: 30000,
    enabled: !!siteId,
  });

  useEffect(() => {
    if (isError) {
      console.error('InlineTimeSeriesChart: timeseries query failed', error);
    }
  }, [isError, error]);

  // Build chart data — merge multiple series by timestamp
  const chartData = (() => {
    if (!data) return [];
    if (multiSeries) {
      const timestampMap: Record<string, any> = {};
      multiSeries.forEach(s => {
        const seriesData = data[s.kpiName] || [];
        seriesData.forEach((point: any) => {
          if (!timestampMap[point.timestamp]) {
            timestampMap[point.timestamp] = { timestamp: point.timestamp };
          }
          timestampMap[point.timestamp][s.kpiName] = point.value;
        });
      });
      return Object.values(timestampMap).sort(
        (a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
    }
    return data[kpiName] || [];
  })();

  const series = multiSeries
    ? multiSeries.map(s => ({
        name: s.name,
        dataKey: s.kpiName,
        color: s.color,
        unit: s.unit,
        strokeWidth: 2,
      }))
    : [{ name: title, dataKey: 'value', color, unit, strokeWidth: 2 }];

  return (
    <div
      className="rounded-xl border border-slate-700/50 p-5 backdrop-blur-md"
      style={{
        background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(51, 65, 85, 0.5) 100%)',
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-white">{title}</h4>
        {effectiveRangeSelectorVisible && (
          <div className="flex gap-1.5">
            {(['1h', '24h', '7d'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                  timeRange === range
                    ? 'bg-djin-cyan text-djin-navy'
                    : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-slate-300'
                }`}
              >
                {range.toUpperCase()}
              </button>
            ))}
          </div>
        )}
      </div>

      {isError && chartData.length === 0 ? (
        <div
          className="bg-slate-800/30 rounded-lg flex items-center justify-center"
          style={{ height }}
          role="alert"
        >
          <p className="text-slate-400">Unable to load chart data</p>
        </div>
      ) : chartType === 'bar' ? (
        <BarChart
          data={chartData}
          series={series}
          height={height}
          yAxisDomain={yAxisDomain}
          showLegend={showLegend}
          loading={isLoading}
        />
      ) : chartType === 'area' ? (
        <AreaChart
          data={chartData}
          series={series}
          height={height}
          yAxisDomain={yAxisDomain}
          showBrush={false}
          showLegend={showLegend}
          loading={isLoading}
        />
      ) : (
        <LineChart
          data={chartData}
          series={series}
          height={height}
          yAxisDomain={yAxisDomain}
          showBrush={false}
          showLegend={showLegend}
          loading={isLoading}
        />
      )}
    </div>
  );
}
